import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortPestSections } from 'App/Map/PanelDetails/_utils/sortPestSections'
import type { StackedChartPestSection, StackedChartSection } from 'components/StackedChart/types'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import * as almondBloom from '../by-domain/almondBloom/almondBloom'
import * as alternaria from '../by-domain/alternaria/alternaria'
import * as beeHours from '../by-domain/beeHours/beeHours'
import * as cherryPowderyMildewPrimaryInfectionMonitoring from '../by-domain/cherryPowderyMildew/primaryInfectionMonitoring/cherryPowderyMildewPrimaryInfectionMonitoring'
import * as cherryPowderyMildewSecondaryInfectionRiskIndex from '../by-domain/cherryPowderyMildew/secondaryInfectionRiskIndex/cherryPowderyMildewSecondaryInfectionRiskIndex'
import * as chill from '../by-domain/chill/chill'
import * as conditions from '../by-domain/conditions/conditions'
import * as dewPoint from '../by-domain/dewPoint/dewPoint'
import * as evapotranspiration from '../by-domain/evapotranspiration/evapotranspiration'
import * as fireBlight from '../by-domain/fireBlight/fireBlight'
import * as fruitGrowth from '../by-domain/fruitGrowth/fruitGrowth'
import * as grapePowderyMildewPrimaryInfectionMonitoring from '../by-domain/grapePowderyMildew/primaryInfectionMonitoring/grapePowderyMildewPrimaryInfectionMonitoring'
import * as grapePowderyMildewSecondaryInfectionRiskIndex from '../by-domain/grapePowderyMildew/secondaryInfectionRiskIndex/grapePowderyMildewSecondaryInfectionRiskIndex'
import * as humidity from '../by-domain/humidity/humidity'
import * as insectLarvalTrend from '../by-domain/insectLarvalTrend/insectLarvalTrend'
import * as insectPhenology from '../by-domain/insectPhenology/insectPhenology'
import * as insectTrapAndDegreeDays from '../by-domain/insectTrapAndDegreeDays/insectTrapAndDegreeDays'
import * as irrigationActivity from '../by-domain/irrigationActivity/irrigationActivity'
import * as leafWetness from '../by-domain/leafWetness/leafWetness'
import * as plantStress from '../by-domain/plantStress/plantStress'
import * as precipitation from '../by-domain/precipitation/precipitation'
import * as soil from '../by-domain/soil/soil'
import * as sprayConditions from '../by-domain/sprayConditions/sprayConditions'
import * as temperature from '../by-domain/temperature/temperature'
import * as wetBulb from '../by-domain/wetBulb/wetBulb'
import * as wind from '../by-domain/wind/wind'
import * as windMachine from '../by-domain/windMachine/windMachine'
import { applyColorPalette } from './_utils/applyColorPalette'

export const createContent = ({
  compareSeasonsData,
  data,
  selectedValueGroups,
  selectedFieldAssets,
  updateData,
}: {
  compareSeasonsData: routes.Values.Response
  data: routes.Values.Response
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  updateData: (pathToSet: string, dataToSet: unknown) => void
}): StackedChartSection[] => {
  const content: StackedChartSection[] = []

  /* ENVIRONMENTAL */
  if (!!selectedValueGroups.conditions) content.push(conditions.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.dew_point) content.push(dewPoint.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.humidity) content.push(humidity.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.precipitation) content.push(precipitation.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.air_temperature) content.push(temperature.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.wet_bulb) content.push(wetBulb.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.wind) content.push(wind.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.leaf_wetness) content.push(leafWetness.content({ data, compareSeasonsData }))

  /* CROP DEVELOPMENT */
  if (!!selectedValueGroups.chill) content.push(chill.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.bee_hours) content.push(beeHours.content({ data, compareSeasonsData }))

  if (!!selectedValueGroups.almond_bloom) content.push(almondBloom.content({ data, compareSeasonsData }))

  /* WATER */
  if (!!selectedValueGroups.evapotranspiration)
    content.push(evapotranspiration.content({ data, compareSeasonsData }))

  // TODO: when there are no soil stations on the property, should we display some kind of no data message like we do for summary grid table?
  if (!!selectedValueGroups.soil) content.push(...soil.content({ data, compareSeasonsData }))

  /* SPRAYS */
  if (!!selectedValueGroups.spray_conditions) content.push(sprayConditions.content({ data }))

  /* FRUIT GROWTH */
  if (!!selectedValueGroups.fruit_growth) content.push(fruitGrowth.content({ data, compareSeasonsData }))

  /* PLANT STRESS */
  if (!!selectedValueGroups.plant_stress)
    content.push(...plantStress.content({ data, compareSeasonsData, updateData }))

  if (
    Object.entries(selectedValueGroups).some(
      ([valueGroup, isActive]) => !!isActive && valueGroup.startsWith('irrigation_activity'),
    )
  ) {
    content.push(...irrigationActivity.content({ data, selectedFieldAssets, updateData }))
  }

  /* Pests */
  const pestContent: StackedChartPestSection[] = []

  if (
    Object.entries(selectedValueGroups).some(
      ([valueGroup, isActive]) =>
        !!isActive &&
        (valueGroup.startsWith('degree_days_insect_id_') || valueGroup.startsWith('trap_catches_insect_id_')),
    )
  ) {
    pestContent.push(...insectTrapAndDegreeDays.content({ data, selectedValueGroups, compareSeasonsData }))
  }

  if (
    Object.entries(selectedValueGroups).some(
      ([valueGroup, isActive]) => !!isActive && valueGroup.startsWith('phenology_insect_id_'),
    )
  ) {
    pestContent.push(...insectPhenology.content({ data, selectedValueGroups }))
  }

  if (
    Object.entries(selectedValueGroups).some(
      ([valueGroup, isActive]) => !!isActive && valueGroup.startsWith('larval_trend_insect_id'),
    )
  )
    pestContent.push(...insectLarvalTrend.content({ data, selectedValueGroups, compareSeasonsData }))

  // it's nice to keep pest information grouped together by pest
  pestContent.sort(sortPestSections)

  content.push(...pestContent)

  // ** EQUIPMENT **

  if (!!selectedValueGroups.wind_machine) content.push(...windMachine.content({ data }))

  /* Diseases */

  if (!!selectedValueGroups.alternaria)
    content.push(alternaria.content({ data, compareSeasonsData, updateData }))

  if (!!selectedValueGroups.cherry_powdery_mildew) {
    content.push(cherryPowderyMildewPrimaryInfectionMonitoring.content({ data, compareSeasonsData }))

    content.push(cherryPowderyMildewSecondaryInfectionRiskIndex.content({ data, compareSeasonsData }))
  }

  if (!!selectedValueGroups.grape_powdery_mildew) {
    content.push(grapePowderyMildewPrimaryInfectionMonitoring.content({ data, compareSeasonsData }))

    content.push(grapePowderyMildewSecondaryInfectionRiskIndex.content({ data, compareSeasonsData }))
  }

  if (!!selectedValueGroups.fire_blight)
    content.push(fireBlight.content({ data, compareSeasonsData, updateData }))

  return applyColorPalette(content).sort((a, b) => {
    // sort here will put items with no data down at the bottom
    return Number(!!a.defaultCollapsed) - Number(!!b.defaultCollapsed)
  })
}
